import { thunk } from 'easy-peasy';
import { API } from '@aws-amplify/api';
import awsApiConfig from './config/awsApiConfigure';

API.configure(awsApiConfig);

/**
 * Échappe une string pour l'injecter dans une requête GraphQL inline.
 * (Si tu passes un JSON variables, on pourrait s'en passer, mais tu es en inline.)
 */
const gqlEscape = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
};

const AuthorizationMiddleware = {
  //✨✨==========================================✨✨
  //         GET LIST AUTHORIZATION TEMPLATES
  //✨✨==========================================✨✨
  // payload: { activeOnly?: boolean, limit?: number, nextToken?: string }
  getListAuthorizationTemplates: thunk(
    async (actions, payload = {}, { getStoreActions }) => {
      const activeOnly = !!payload.activeOnly;
      const limit = payload.limit || 200;
      const nextToken = payload.nextToken ? `"${gqlEscape(payload.nextToken)}"` : null;

      const filterPart = activeOnly
        ? `filter: { isActive: { eq: true } }`
        : '';

      const nextTokenPart = nextToken ? `, nextToken: ${nextToken}` : '';

      const graphqlQuery = `
        query MyQuery {
          listAuthorizationTemplates(${filterPart}${filterPart ? ',' : ''} limit: ${limit}${nextTokenPart}) {
            items {
              id
              title
              bodyText
              isActive
              category
              version
              createdAt
              updatedAt
            }
            nextToken
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.listAuthorizationTemplates;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //              GET AUTHORIZATION TEMPLATE
  //✨✨==========================================✨✨
  // payload: templateId
  getAuthorizationTemplate: thunk(
    async (actions, payload, { getStoreActions }) => {
      const graphqlQuery = `
        query MyQuery {
          getAuthorizationTemplate(id: "${gqlEscape(payload)}") {
            id
            title
            bodyText
            isActive
            category
            version
            createdAt
            updatedAt
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.getAuthorizationTemplate;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //           CREATE AUTHORIZATION TEMPLATE
  //✨✨==========================================✨✨
  // payload: { title, bodyText, category?, version?, isActive? }
  createAuthorizationTemplate: thunk(
    async (actions, payload, { getStoreActions }) => {
      const title = gqlEscape(payload?.title);
      const bodyText = gqlEscape(payload?.bodyText);
      const category = payload?.category ? `"${gqlEscape(payload.category)}"` : null;
      const version = Number.isFinite(payload?.version) ? payload.version : null;
      const isActive = payload?.isActive === false ? 'false' : 'true';

      const graphqlQuery = `
        mutation MyMutation {
          createAuthorizationTemplate(input: {
            title: "${title}"
            bodyText: "${bodyText}"
            isActive: ${isActive}
            ${category ? `category: ${category}` : ''}
            ${version !== null ? `version: ${version}` : ''}
          }) {
            id
            title
            isActive
            createdAt
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.createAuthorizationTemplate;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //           UPDATE AUTHORIZATION TEMPLATE
  //✨✨==========================================✨✨
  // payload: { id, title?, bodyText?, category?, version?, isActive? }
  updateAuthorizationTemplate: thunk(
    async (actions, payload, { getStoreActions }) => {
      const id = gqlEscape(payload?.id);
      const hasTitle = payload?.title !== undefined;
      const hasBody = payload?.bodyText !== undefined;
      const hasCategory = payload?.category !== undefined;
      const hasVersion = payload?.version !== undefined;
      const hasIsActive = payload?.isActive !== undefined;

      const parts = [
        `id: "${id}"`,
        hasTitle ? `title: "${gqlEscape(payload.title)}"` : null,
        hasBody ? `bodyText: "${gqlEscape(payload.bodyText)}"` : null,
        hasCategory
          ? payload.category === null || payload.category === ''
            ? `category: null`
            : `category: "${gqlEscape(payload.category)}"`
          : null,
        hasVersion
          ? Number.isFinite(payload.version)
            ? `version: ${payload.version}`
            : `version: null`
          : null,
        hasIsActive ? `isActive: ${payload.isActive ? 'true' : 'false'}` : null,
      ].filter(Boolean);

      const graphqlQuery = `
        mutation MyMutation {
          updateAuthorizationTemplate(input: {
            ${parts.join('\n            ')}
          }) {
            id
            title
            bodyText
            isActive
            category
            version
            updatedAt
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.updateAuthorizationTemplate;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //        TOGGLE AUTHORIZATION TEMPLATE ACTIVE
  //✨✨==========================================✨✨
  // payload: { id, isActive }
  toggleAuthorizationTemplateActive: thunk(
    async (actions, payload, { getStoreActions }) => {
      const id = gqlEscape(payload?.id);
      const isActive = payload?.isActive ? 'true' : 'false';

      const graphqlQuery = `
        mutation MyMutation {
          updateAuthorizationTemplate(input: {
            id: "${id}"
            isActive: ${isActive}
          }) {
            id
            isActive
            updatedAt
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.updateAuthorizationTemplate;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //              DELETE AUTHORIZATION TEMPLATE
  //✨✨==========================================✨✨
  // payload: templateId
  deleteAuthorizationTemplate: thunk(
    async (actions, payload, { getStoreActions }) => {
      const graphqlQuery = `
        mutation MyMutation {
          deleteAuthorizationTemplate(input: { id: "${gqlEscape(payload)}" }) {
            id
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.deleteAuthorizationTemplate;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

  //✨✨==========================================✨✨
  //        GET LIST AUTHORIZATION CONSENTS (ADMIN)
  //✨✨==========================================✨✨
  // payload: { status?, limit?, nextToken? }
  getListAuthorizationConsents: thunk(
    async (actions, payload = {}, { getStoreActions }) => {
      const limit = payload.limit || 100;
      const nextToken = payload.nextToken
        ? `"${gqlEscape(payload.nextToken)}"`
        : null;

      const filterPart = payload.status
        ? `filter: { status: { eq: ${payload.status} } }`
        : '';

      const nextTokenPart = nextToken ? `, nextToken: ${nextToken}` : '';

      const graphqlQuery = `
        query MyQuery {
          listAuthorizationConsents(
            ${filterPart}
            limit: ${limit}
            ${nextTokenPart}
          ) {
            items {
              id
              status
              technicianId
              tenantName
              tenantEmail
              apartmentNumber
              templateId
              pdfKey
              signedAt
              createdAt
              updatedAt
            }
            nextToken
          }
        }
      `;

      getStoreActions().global.setActivity(true);
      try {
        const query = await API.graphql({ query: graphqlQuery });
        return query.data?.listAuthorizationConsents;
      } finally {
        getStoreActions().global.setActivity(false);
      }
    }
  ),

};

export default AuthorizationMiddleware;
